<template>
  <div>
    <Head title="Hasil Verifikasi Pertanggung Jawaban" />
    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/verified-accountability-reports">
        Hasil Verifikasi
        Pertanggung Jawaban
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ application.nama }}
    </h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="flex flex-col w-full px-8 py-6 space-y-2 bg-gray-200 md:flex-row md:justify-between md:space-y-0">
        <h2 class="text-xl">Informasi Pertanggung Jawaban ({{ application.jenis }})</h2>
        <div>
          <Button
            v-if="canWrite('verify permohonan beasiswa')" type="button" class="mr-2 btn-warning"
            @click.prevent="unverifiedApplication"
          >
            <span><font-awesome-icon icon="fas fa-close" class="mr-2" />Unverified</span>
          </Button>
          <span v-if="application.status == 2" class="px-4 py-2 text-sm bg-red-300 rounded-lg">Belum Memenuhi
            Syarat</span>
          <span v-else-if="application.status == 3" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi
            Syarat</span>
          <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
        </div>
      </div>
      <div class="flex w-full">
        <dl class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-3/12">Periode Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ application.periode }}</dd>
          <dt class="w-full lg:w-3/12">NIM Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nim }}</dd>
          <dt class="w-full lg:w-3/12">Nama Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama }}</dd>
          <dt class="w-full lg:w-3/12">Nama Universitas</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_universitas }}</dd>
          <dt class="w-full lg:w-3/12">Program Studi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_prodi }}</dd>
          <dt class="w-full lg:w-3/12">Jenis Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ application.jenis }}</dd>
        </dl>
        <div class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-4/12">Semester</dt>
          <dd class="w-full lg:w-8/12">: {{ application.semester }}</dd>
          <dt class="w-full lg:w-4/12">Nilai IPK</dt>
          <dd class="w-full lg:w-8/12">: {{ application.ipk }}</dd>
          <dt class="w-full lg:w-4/12">No Rekening</dt>
          <dd class="w-full lg:w-8/12">: {{ application.account_number }}</dd>
          <dt class="w-full lg:w-4/12">Biaya Pendidikan</dt>
          <dd class="w-full lg:w-8/12">: Rp. {{ $filters.formatNumber(application.tuition_fee) }}</dd>
          <dt class="w-full lg:w-4/12">Biaya Lainnya</dt>
          <dd class="w-full lg:w-8/12">: Rp. {{ $filters.formatNumber(application.other_fee) }}</dd>
        </div>
      </div>
      <div
        v-if="application.catatan_verifikasi && (application.status == 2 || application.status == 3)"
        class="w-full p-8 text-base text-center border-t border-gray-200"
      >
        <h2>Catatan Verifikasi: </h2>
        {{ application.catatan_verifikasi }}
        <p class="mt-2">
          Diverifikasi Oleh: {{ application.verifikator }}
        </p>
      </div>
    </div>
    <div class="w-full bg-white rounded-md shadow">
      <div class="flex w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">File Persyaratan</h2>
      </div>
      <div class="p-6">
        <ul class="mt-2 ml-4 list-disc">
          <li
            v-for="file in application.files" :key="`${file.id}-list`"
            :class="{ 'form-warning': file.status == 1, 'form-valid': file.status == 4, 'form-error': file.status == 2 || file.status == 3 }"
          >
            {{ file.persyaratan }} <font-awesome-icon
              v-if="file.status == 4" icon="fas fa-check"
              class="text-green-700"
            />
            <span v-if="file.status == 1" class="ml-1 form-warning">(Belum Verifikasi)</span>
          </li>
        </ul>
      </div>
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis File</th>
            <th class="px-6 pt-6 pb-4 text-center">Status Verifikasi</th>
            <th v-if="can('write pertanggung jawaban')" class="px-6 pt-6 pb-4 border-b">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-4 border-t w-">
              {{ file.persyaratan }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="file.status == 2" class="px-4 py-2 text-sm bg-red-300 rounded-lg">Belum Memenuhi Syarat</span>
                <span v-else-if="file.status == 3" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi
                  Syarat</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-2 leading-4 break-normal">{{ file.catatan_verifikasi }}</p>
            </td>
            <td class="w-px p-4 border-t">
              <Button
                type="button" title="Lihat File" class="rounded btn-green-sm"
                @click.prevent="viewImage(file.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.files.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data file persayaratan yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="w-full mt-8 bg-white rounded-md shadow">
      <div class="w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">History Pertanggung Jawaban</h2>
      </div>
      <table class="w-full">
        <thead>
          <tr class="font-bold text-left">
            <th class="w-3/12 px-6 pt-6 pb-4">Waktu</th>
            <th class="w-3/12 px-6 pt-6 pb-4 text-center">Jenis Aksi</th>
            <th class="w-6/12 px-6 pt-6 pb-4 border-b">Catatan</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="history in application.histories" :key="history.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="p-4 border-t">
              {{ history.waktu }}
            </td>
            <td class="p-4 text-center border-t">
              {{ history.action }}
            </td>
            <td class="w-px p-4 border-t">
              {{ history.catatan }}
            </td>
          </tr>
          <tr v-if="application.histories.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data history.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: {
    Head,
    Link,
    ImageViewer,
  },
  layout: Layout,
  props: {
    application: Object,
  },
  remember: 'form',
  data() {
    return {
      imageSource: '',
      showImage: false,
    }
  },
  methods: {
    unverifiedApplication() {
      this.$swal.fire({
        title: 'Anda yakin unverified pertanggung jawaban ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/accountability-reports/${this.application.id}/unverified`)
        }
      })
    },
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
  },
}
</script>
